<template>
  <v-container>
    <v-snackbar v-model="snackbar" :color="snackbarColor" timeout="3500" location="top right">
      {{ snackbarText }}
      <template v-slot:actions>
        <v-btn icon="mdi-close" variant="text" @click="snackbar = false" />
      </template>
    </v-snackbar>

    <!-- ── Add Event Dialog ──────────────────────────────── -->
    <v-dialog v-model="dialog" max-width="680" persistent>
      <template v-slot:activator="{ props: activatorProps }">
        <v-btn
          prepend-icon="mdi-calendar-plus"
          text="New Event"
          color="grey-darken-3"
          block
          v-bind="activatorProps"
          class="mb-4"
          rounded="lg"
        />
      </template>

      <v-card rounded="xl">
        <!-- Dialog header -->
        <div class="d-flex align-center pa-5 pb-3" style="gap: 12px;">
          <v-avatar color="grey-darken-3" size="40" rounded="lg">
            <v-icon color="white">mdi-calendar-plus</v-icon>
          </v-avatar>
          <div>
            <div class="text-h6 font-weight-bold">New Event</div>
            <div class="text-caption text-medium-emphasis">Fill in the details to create a new event</div>
          </div>
          <v-spacer />
          <v-btn icon="mdi-close" variant="text" size="small" @click="dialog = false" />
        </div>

        <v-divider />

        <v-card-text class="pa-5">
          <v-form ref="form" v-model="valid">

            <!-- Event name -->
            <div class="text-caption font-weight-bold text-medium-emphasis text-uppercase mb-1 d-flex align-center" style="gap: 4px;">
              <v-icon size="13">mdi-tag-outline</v-icon> Event Details
            </div>
            <v-text-field
              v-model="event.name"
              label="Event Name"
              placeholder="e.g. Annual Gala 2026"
              :rules="nameRules"
              variant="outlined"
              density="comfortable"
              prepend-inner-icon="mdi-calendar-star"
              class="mb-3"
            />

            <!-- Date + Max invitations row -->
            <v-row dense class="mb-1">
              <v-col cols="12" sm="7">
                <v-date-input
                  v-model="event.date"
                  label="Event Date"
                  color="grey-darken-3"
                  variant="outlined"
                  density="comfortable"
                  prepend-inner-icon="mdi-calendar"
                  :rules="[(v) => !!v || 'Date is required']"
                />
              </v-col>
              <v-col cols="12" sm="5">
                <v-number-input
                  v-model="event.max_invitations"
                  label="Max Seats"
                  controlVariant="stacked"
                  :rules="maxInvitationsRules"
                  variant="outlined"
                  density="comfortable"
                  :min="1"
                />
              </v-col>
            </v-row>

            <!-- Description -->
            <v-textarea
              v-model="event.description"
              label="Description"
              placeholder="What is this event about?"
              :rules="descRules"
              variant="outlined"
              density="comfortable"
              rows="3"
              prepend-inner-icon="mdi-text"
              class="mb-4"
            />

            <v-divider class="mb-4" />

            <!-- Images -->
            <div class="text-caption font-weight-bold text-medium-emphasis text-uppercase mb-3 d-flex align-center" style="gap: 4px;">
              <v-icon size="13">mdi-image-outline</v-icon> Images (optional)
            </div>
            <v-row dense class="mb-3">
              <v-col cols="12" sm="6">
                <div class="text-caption text-medium-emphasis mb-1">Cover Image</div>
                <v-file-upload
                  density="comfortable"
                  variant="outlined"
                  color="grey-darken-3"
                  v-model="event.file"
                  accept="image/*"
                />
                <v-img
                  v-if="event.filePreview"
                  :src="event.filePreview"
                  height="80"
                  cover
                  rounded="lg"
                  class="mt-2"
                />
              </v-col>
              <v-col cols="12" sm="6">
                <div class="text-caption text-medium-emphasis mb-1">Second Image</div>
                <v-file-upload
                  density="comfortable"
                  variant="outlined"
                  color="grey-darken-3"
                  v-model="event.file2"
                  accept="image/*"
                />
                <v-img
                  v-if="event.file2Preview"
                  :src="event.file2Preview"
                  height="80"
                  cover
                  rounded="lg"
                  class="mt-2"
                />
              </v-col>
            </v-row>

            <v-divider class="mb-3" />

            <!-- Status -->
            <v-switch
              v-model="event.status"
              :true-value="1"
              :false-value="0"
              color="success"
              hide-details
              density="compact"
              :label="event.status ? 'Active — visible and accepting registrations' : 'Inactive — hidden from registration'"
            />
          </v-form>
        </v-card-text>

        <v-divider />

        <v-card-actions class="pa-4">
          <v-spacer />
          <v-btn variant="text" color="grey" @click="dialog = false">Cancel</v-btn>
          <v-btn
            color="grey-darken-3"
            variant="flat"
            rounded="lg"
            prepend-icon="mdi-check"
            :loading="submitting"
            @click="createEvent"
          >
            Create Event
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ── Edit Event Dialog ─────────────────────────────── -->
    <v-dialog v-model="editDialog" max-width="680" persistent>
      <v-card rounded="xl">
        <!-- Dialog header -->
        <div class="d-flex align-center pa-5 pb-3" style="gap: 12px;">
          <v-avatar color="blue" size="40" rounded="lg">
            <v-icon color="white">mdi-calendar-edit</v-icon>
          </v-avatar>
          <div>
            <div class="text-h6 font-weight-bold">Edit Event</div>
            <div class="text-caption text-medium-emphasis">{{ selectedEvent.name }}</div>
          </div>
          <v-spacer />
          <v-btn icon="mdi-close" variant="text" size="small" @click="editDialog = false" />
        </div>

        <v-divider />

        <v-card-text class="pa-5">
          <v-form ref="editForm" v-model="valid">

            <div class="text-caption font-weight-bold text-medium-emphasis text-uppercase mb-1 d-flex align-center" style="gap: 4px;">
              <v-icon size="13">mdi-tag-outline</v-icon> Event Details
            </div>
            <v-text-field
              v-model="selectedEvent.name"
              label="Event Name"
              :rules="nameRules"
              variant="outlined"
              density="comfortable"
              prepend-inner-icon="mdi-calendar-star"
              class="mb-3"
            />

            <v-row dense class="mb-1">
              <v-col cols="12" sm="7">
                <v-date-input
                  v-model="selectedEvent.date"
                  label="Event Date"
                  color="grey-darken-3"
                  variant="outlined"
                  density="comfortable"
                  prepend-inner-icon="mdi-calendar"
                  :rules="[(v) => !!v || 'Date is required']"
                />
              </v-col>
              <v-col cols="12" sm="5">
                <v-number-input
                  v-model="selectedEvent.max_invitations"
                  label="Max Seats"
                  controlVariant="stacked"
                  :rules="maxInvitationsRules"
                  variant="outlined"
                  density="comfortable"
                  :min="1"
                />
              </v-col>
            </v-row>

            <v-textarea
              v-model="selectedEvent.description"
              label="Description"
              :rules="descRules"
              variant="outlined"
              density="comfortable"
              rows="3"
              prepend-inner-icon="mdi-text"
              class="mb-4"
            />

            <v-divider class="mb-4" />

            <!-- Images -->
            <div class="text-caption font-weight-bold text-medium-emphasis text-uppercase mb-3 d-flex align-center" style="gap: 4px;">
              <v-icon size="13">mdi-image-outline</v-icon> Images
            </div>
            <v-row dense class="mb-1">
              <v-col cols="12" sm="6">
                <div class="text-caption text-medium-emphasis mb-1">Cover Image</div>
                <v-img
                  v-if="selectedEvent.image && !selectedEvent.file"
                  :src="'/uploads/' + selectedEvent.image"
                  height="80" cover rounded="lg" class="mb-2"
                />
                <v-file-upload
                  density="comfortable"
                  variant="outlined"
                  color="grey-darken-3"
                  v-model="selectedEvent.file"
                  accept="image/*"
                />
              </v-col>
              <v-col cols="12" sm="6">
                <div class="text-caption text-medium-emphasis mb-1">Second Image</div>
                <v-img
                  v-if="selectedEvent.image2 && !selectedEvent.file2"
                  :src="'/uploads/' + selectedEvent.image2"
                  height="80" cover rounded="lg" class="mb-2"
                />
                <v-file-upload
                  density="comfortable"
                  variant="outlined"
                  color="grey-darken-3"
                  v-model="selectedEvent.file2"
                  accept="image/*"
                />
              </v-col>
            </v-row>

            <v-divider class="mb-3 mt-2" />

            <v-switch
              v-model="selectedEvent.status"
              :true-value="1"
              :false-value="0"
              color="success"
              hide-details
              density="compact"
              :label="selectedEvent.status ? 'Active — visible and accepting registrations' : 'Inactive — hidden from registration'"
            />
          </v-form>
        </v-card-text>

        <v-divider />

        <v-card-actions class="pa-4">
          <v-spacer />
          <v-btn variant="text" color="grey" @click="editDialog = false">Cancel</v-btn>
          <v-btn
            color="blue"
            variant="flat"
            rounded="lg"
            prepend-icon="mdi-content-save"
            :loading="submitting"
            @click="submitEdit"
          >
            Save Changes
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Search / Filter / View Toggle toolbar -->
    <v-row align="center" class="mb-4" dense>
      <v-col cols="12" sm="5">
        <v-text-field
          v-model="searchText"
          prepend-inner-icon="mdi-magnify"
          label="Search events"
          variant="outlined"
          density="compact"
          hide-details
          clearable
        />
      </v-col>
      <v-col cols="12" sm="4">
        <v-select
          v-model="statusFilter"
          :items="statusOptions"
          item-value="value"
          item-title="label"
          label="Status"
          variant="outlined"
          density="compact"
          hide-details
          clearable
        />
      </v-col>
      <v-col cols="auto">
        <v-btn-toggle v-model="viewMode" color="grey-darken-3" border group density="comfortable">
          <v-btn value="table" icon="mdi-table" size="small" />
          <v-btn value="cards" icon="mdi-view-grid-outline" size="small" />
        </v-btn-toggle>
      </v-col>
    </v-row>

    <!-- ── TABLE VIEW ─────────────────────────────── -->
    <template v-if="viewMode === 'table'">
      <v-card variant="outlined" class="pa-2">
        <v-card-title class="text-subtitle-1 font-weight-medium">
          Events List
          <v-chip size="x-small" color="grey" variant="tonal" class="ml-2">{{ filteredEvents.length }}</v-chip>
        </v-card-title>
        <v-data-table :headers="headers" :items="filteredEvents" item-key="id" fixed-header density="compact">

          <template v-slot:[`item.image`]="{ item }">
            <v-img :src="'/uploads/' + item.image" width="60" height="40" cover rounded="md" v-if="item.image" class="my-1" />
            <v-img v-else src="https://placehold.co/60x40?text=—" width="60" height="40" rounded="md" cover class="my-1" />
          </template>

          <template v-slot:[`item.image2`]="{ item }">
            <v-img :src="'/uploads/' + item.image2" width="60" height="40" cover rounded="md" v-if="item.image2" class="my-1" />
            <v-img v-else src="https://placehold.co/60x40?text=—" width="60" height="40" rounded="md" cover class="my-1" />
          </template>

          <template v-slot:[`item.description`]="{ item }">
            <span class="text-caption text-medium-emphasis">
              {{ item.description && item.description.length > 40 ? item.description.substring(0, 40) + '…' : item.description }}
            </span>
          </template>

          <template v-slot:[`item.status`]="{ item }">
            <v-chip :color="item.status ? 'success' : 'grey'" size="x-small" variant="tonal" label>
              {{ item.status ? 'Active' : 'Inactive' }}
            </v-chip>
          </template>

          <template v-slot:[`item.attendee_count`]="{ item }">
            <v-chip size="x-small" color="blue" variant="tonal" prepend-icon="mdi-account-group">
              {{ item.attendee_count }}
            </v-chip>
          </template>

          <template v-slot:[`item.checked_in_count`]="{ item }">
            <v-chip size="x-small" color="green" variant="tonal" prepend-icon="mdi-check-circle-outline">
              {{ item.checked_in_count }}
            </v-chip>
          </template>

          <template v-slot:[`item.capacity`]="{ item }">
            <div style="min-width: 110px;">
              <div class="d-flex justify-space-between text-caption mb-1">
                <span>{{ item.attendee_count }} / {{ item.max_invitations }}</span>
                <span :class="capacityColor(item) + '--text'">{{ capacityPct(item) }}%</span>
              </div>
              <v-progress-linear
                :model-value="capacityPct(item)"
                :color="capacityColor(item)"
                height="5"
                rounded
                bg-color="grey-lighten-3"
              />
            </div>
          </template>

          <template v-slot:[`item.date`]="{ item }">
            <span class="text-caption">{{ formatDateTime(item.date) }}</span>
          </template>

          <template v-slot:[`item.created_at`]="{ item }">
            <span class="text-caption text-medium-emphasis">{{ formatDateTime(item.created_at) }}</span>
          </template>

          <template v-slot:[`item.actions`]="{ item }">
            <div class="d-flex align-center" style="gap: 4px;">
              <router-link :to="{ name: 'event-detail', params: { id: item.id } }">
                <v-btn color="grey-darken-3" icon="mdi-eye" size="small" variant="tonal" />
              </router-link>
              <v-btn @click="openEditDialog(item)" icon="mdi-pencil" color="blue" size="small" variant="tonal" />
              <v-btn @click="deleteEvent(item.id)" icon="mdi-delete" color="red" size="small" variant="tonal" />
            </div>
          </template>
        </v-data-table>
      </v-card>
    </template>

    <!-- ── CARD VIEW ──────────────────────────────── -->
    <template v-else>
      <v-row>
        <v-col v-for="event in filteredEvents" :key="event.id" cols="12" sm="6" md="4">
          <v-card class="event-card" rounded="xl" elevation="2">

            <!-- Image with overlaid badges -->
            <div style="position: relative; overflow: hidden; height: 200px;">
              <v-img
                v-if="event.image"
                :src="'/uploads/' + event.image"
                height="200"
                cover
                gradient="to bottom, transparent 40%, rgba(0,0,0,0.75) 100%"
              />
              <v-img
                v-else
                src="https://placehold.co/700x400?text=No+Image"
                height="200"
                cover
              />

              <!-- Status chip top-right -->
              <v-chip
                :color="event.status ? 'success' : 'grey'"
                size="x-small"
                variant="elevated"
                label
                style="position: absolute; top: 10px; right: 10px;"
              >
                {{ event.status ? 'Active' : 'Inactive' }}
              </v-chip>

              <!-- Title overlay -->
              <div style="position: absolute; bottom: 0; left: 0; right: 0; padding: 12px 16px;">
                <div class="text-white font-weight-bold text-subtitle-1 text-truncate">{{ event.name }}</div>
                <div class="text-white text-caption" style="opacity: 0.85;">
                  <v-icon size="12" class="mr-1">mdi-calendar</v-icon>{{ formatDateTime(event.date, 'YYYY-MM-DD') }}
                </div>
              </div>
            </div>

            <!-- Body -->
            <v-card-text class="pb-2">
              <p class="text-body-2 text-medium-emphasis text-truncate mb-3">{{ event.description }}</p>
              <div class="d-flex align-center mb-2" style="gap: 8px;">
                <v-chip size="small" color="blue" variant="tonal" prepend-icon="mdi-account-group">
                  {{ event.attendee_count }} / {{ event.max_invitations }}
                </v-chip>
                <v-chip size="small" color="green" variant="tonal" prepend-icon="mdi-check-circle-outline">
                  {{ event.checked_in_count }} checked in
                </v-chip>
              </div>
              <div>
                <div class="d-flex justify-space-between text-caption mb-1">
                  <span class="text-medium-emphasis">Capacity</span>
                  <span :style="{ color: capacityHex(event) }">{{ capacityPct(event) }}%</span>
                </div>
                <v-progress-linear
                  :model-value="capacityPct(event)"
                  :color="capacityColor(event)"
                  height="6"
                  rounded
                  bg-color="grey-lighten-3"
                />
              </div>
            </v-card-text>

            <v-divider />

            <!-- Actions -->
            <v-card-actions class="pa-3">
              <router-link :to="{ name: 'event-detail', params: { id: event.id } }" style="text-decoration: none;">
                <v-btn color="grey-darken-3" variant="tonal" size="small" prepend-icon="mdi-eye">View</v-btn>
              </router-link>
              <v-spacer />
              <v-btn @click="openEditDialog(event)" icon="mdi-pencil" color="blue" size="small" variant="text" />
              <v-btn @click="deleteEvent(event.id)" icon="mdi-delete" color="red" size="small" variant="text" />
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>
    </template>
  </v-container>
</template>

<script>
import axios from "axios";

export default {
  data() {
    return {
      dialog: false,
      valid: false,
      viewMode: "table",
      editDialog: false,
      submitting: false,
      snackbar: false,
      snackbarText: "",
      snackbarColor: "success",
      searchText: "",
      statusFilter: null,
      statusOptions: [
        { label: "Active",   value: 1 },
        { label: "Inactive", value: 0 },
      ],
      selectedEvent: {
        id: null, name: "", description: "", date: null,
        file: null, file2: null, max_invitations: 0, status: 0,
        image: null, image2: null,
      },
      event: {
        name: "", description: "", date: new Date(),
        file: null, file2: null, filePreview: null, file2Preview: null,
        max_invitations: 0, status: 1,
      },
      events: [],
      nameRules: [(v) => !!v || "Name is required"],
      descRules:  [(v) => !!v || "Description is required"],
      maxInvitationsRules: [
        (v) => !!v || "Required",
        (v) => v > 0 || "Must be greater than 0",
      ],
      headers: [
        { title: "Image",          value: "image",           sortable: false },
        { title: "Image 2",        value: "image2",          sortable: false },
        { title: "Name",           value: "name",            sortable: true  },
        { title: "Description",    value: "description",     sortable: false },
        { title: "Capacity",       value: "capacity",        sortable: false },
        { title: "Attendees",      value: "attendee_count",  sortable: true  },
        { title: "Checked In",     value: "checked_in_count",sortable: true  },
        { title: "Status",         value: "status",          sortable: true  },
        { title: "Date",           value: "date",            sortable: true  },
        { title: "Created",        value: "created_at",      sortable: true  },
        { title: "Actions",        value: "actions",         sortable: false },
      ],
    };
  },
  mounted() {
    this.fetchEvents();
  },

  watch: {
    "event.file"(f) {
      this.event.filePreview = f ? URL.createObjectURL(f) : null;
    },
    "event.file2"(f) {
      this.event.file2Preview = f ? URL.createObjectURL(f) : null;
    },
    dialog(open) {
      if (open) {
        this.event = {
          name: "", description: "", date: new Date(),
          file: null, file2: null, filePreview: null, file2Preview: null,
          max_invitations: 0, status: 1,
        };
        this.$nextTick(() => this.$refs.form?.resetValidation());
      }
    },
  },
  computed: {
    filteredEvents() {
      return this.events.filter((e) => {
        const q = (this.searchText || "").toLowerCase();
        const matchSearch =
          !q ||
          (e.name || "").toLowerCase().includes(q) ||
          (e.description || "").toLowerCase().includes(q);
        const matchStatus =
          this.statusFilter === null ||
          this.statusFilter === undefined ||
          e.status == this.statusFilter;
        return matchSearch && matchStatus;
      });
    },
  },

  methods: {
    capacityPct(event) {
      if (!event.max_invitations || event.max_invitations === 0) return 0;
      return Math.min(100, Math.round((event.attendee_count / event.max_invitations) * 100));
    },
    capacityColor(event) {
      const pct = this.capacityPct(event);
      if (pct >= 90) return "red";
      if (pct >= 60) return "orange";
      return "green";
    },
    capacityHex(event) {
      const pct = this.capacityPct(event);
      if (pct >= 90) return "#f44336";
      if (pct >= 60) return "#ff9800";
      return "#4caf50";
    },
    formatDateTime(dateTime, format = "YYYY-MM-DD HH:mm") {
      if (!dateTime) return "";
      const date = new Date(dateTime);
      const pad  = (n) => n.toString().padStart(2, "0");
      const year = date.getFullYear();
      const month  = pad(date.getMonth() + 1);
      const day    = pad(date.getDate());
      const hours  = pad(date.getHours());
      const mins   = pad(date.getMinutes());
      return format === "YYYY-MM-DD"
        ? `${year}-${month}-${day}`
        : `${year}-${month}-${day} ${hours}:${mins}`;
    },
    notify(text, color = "success") {
      this.snackbarText  = text;
      this.snackbarColor = color;
      this.snackbar      = true;
    },

    async fetchEvents() {
      try {
        const response = await axios.get("/api/events");
        this.events = response.data;
      } catch (error) {
        console.error("Error fetching events:", error);
      }
    },

    async createEvent() {
      const { valid } = await this.$refs.form.validate();
      if (!valid) return;
      this.submitting = true;
      const formData  = new FormData();
      formData.append("name",            this.event.name);
      formData.append("description",     this.event.description);
      formData.append("date",            this.formatDateTime(this.event.date, "YYYY-MM-DD"));
      formData.append("max_invitations", this.event.max_invitations);
      formData.append("status",          this.event.status);
      if (this.event.file)  formData.append("file",  this.event.file);
      if (this.event.file2) formData.append("file2", this.event.file2);
      try {
        await axios.post("/api/events", formData, { headers: { "Content-Type": "multipart/form-data" } });
        await this.fetchEvents();
        this.dialog = false;
        this.event  = {
          name: "", description: "", date: new Date(),
          file: null, file2: null, filePreview: null, file2Preview: null,
          max_invitations: 0, status: 1,
        };
        this.$refs.form?.resetValidation();
        this.notify("Event created successfully.");
      } catch (error) {
        this.notify(error.response?.data?.error || "Failed to create event.", "red");
      } finally {
        this.submitting = false;
      }
    },

    async deleteEvent(id) {
      if (!confirm("Delete this event? This cannot be undone.")) return;
      try {
        await axios.delete(`/api/events/${id}`);
        await this.fetchEvents();
        this.notify("Event deleted.");
      } catch (error) {
        this.notify("Failed to delete event.", "red");
      }
    },

    async updateEvent() {
      const formData = new FormData();
      formData.append("name",            this.selectedEvent.name);
      formData.append("description",     this.selectedEvent.description);
      formData.append("date",            this.formatDateTime(this.selectedEvent.date, "YYYY-MM-DD"));
      formData.append("status",          this.selectedEvent.status);
      formData.append("max_invitations", this.selectedEvent.max_invitations);
      if (this.selectedEvent.file)  formData.append("file",  this.selectedEvent.file);
      if (this.selectedEvent.file2) formData.append("file2", this.selectedEvent.file2);
      try {
        await axios.put(`/api/events/${this.selectedEvent.id}`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        this.editDialog = false;
        await this.fetchEvents();
        this.notify("Event updated successfully.");
      } catch (error) {
        this.notify(error.response?.data?.error || "Failed to update event.", "red");
      } finally {
        this.submitting = false;
      }
    },

    openEditDialog(item) {
      this.selectedEvent = {
        ...item,
        max_invitations: item.max_invitations * 1,
        date: item.date ? new Date(item.date) : new Date(),
        file: null,
        file2: null,
      };
      this.$nextTick(() => this.$refs.editForm?.resetValidation());
      this.editDialog = true;
    },

    async submitEdit() {
      const { valid } = await this.$refs.editForm.validate();
      if (!valid) return;
      this.submitting = true;
      await this.updateEvent();
    },
  },
};
</script>

<style scoped>
.event-card {
  transition: transform 0.18s ease, box-shadow 0.18s ease;
}
.event-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.14) !important;
}
</style>
